// src/pages/qc/EquipmentCreateModal.jsx
import { useState } from "react";

const FREQ_OPTIONS = [
  "Once a month",
  "Once in 3 months",
  "Twice in a year",
  "Once a year",
  "As and when required",
  "Not Required",
];

export function EquipmentCreateModal({ equipment, onSave, onCancel }) {
  const isEdit = !!equipment;
  const [form, setForm] = useState({
    equipmentNo:          equipment?.equipmentNo          ?? "",
    name:                 equipment?.name                 ?? "",
    quantity:             equipment?.quantity             ?? 1,
    maker:                equipment?.maker                ?? "",
    range:                equipment?.range                ?? "",
    leastCount:           equipment?.leastCount           ?? "",
    calibrationRequired:  equipment?.calibrationRequired  ?? true,
    calibrationFrequency: equipment?.calibrationFrequency ?? "",
    calibrationType:      equipment?.calibrationType      ?? "",
    expiryDate:           equipment?.expiryDate           ?? "",
    notes:                equipment?.notes                ?? "",
    location:             equipment?.location             ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        ...form,
        quantity:    Number(form.quantity) || 1,
        expiryDate:  form.expiryDate  || null,
        calibrationFrequency: form.calibrationFrequency || null,
        calibrationType:      form.calibrationType      || null,
      };
      await onSave(body);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal" style={{ width: 620 }}>
        <div className="modal-header">
          <div className="modal-title">{isEdit ? "Edit Equipment" : "Add Equipment"}</div>
          <button className="modal-close" onClick={onCancel}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">

            <div style={{ fontWeight: 600, fontSize: 12, color: "#1a3a6b",
              borderBottom: "2px solid #1a3a6b", marginBottom: 12, paddingBottom: 4 }}>
              Equipment Details
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Equipment No</label>
                <input className="form-input" value={form.equipmentNo}
                  onChange={e => set("equipmentNo", e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 2 }}>
                <label className="form-label">Name *</label>
                <input className="form-input" required value={form.name}
                  onChange={e => set("name", e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Quantity</label>
                <input className="form-input" type="number" min={1} value={form.quantity}
                  onChange={e => set("quantity", e.target.value)} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Maker / Brand</label>
                <input className="form-input" value={form.maker}
                  onChange={e => set("maker", e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Range</label>
                <input className="form-input" value={form.range}
                  onChange={e => set("range", e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Least Count</label>
                <input className="form-input" value={form.leastCount}
                  onChange={e => set("leastCount", e.target.value)} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Location</label>
                <input className="form-input" list="location-options" placeholder="e.g. QC Lab, R&D Lab…"
                  value={form.location} onChange={e => set("location", e.target.value)} />
                <datalist id="location-options">
                  <option value="QC Lab" />
                  <option value="R&D Lab" />
                  <option value="Production" />
                  <option value="Maintenance" />
                  <option value="Warehouse" />
                </datalist>
              </div>
            </div>

            <div style={{ fontWeight: 600, fontSize: 12, color: "#1a3a6b",
              borderBottom: "2px solid #1a3a6b", marginBottom: 12, paddingBottom: 4, marginTop: 16 }}>
              Calibration Settings
            </div>

            <div className="form-row" style={{ alignItems: "center" }}>
              <div className="form-group" style={{ flex: 0, minWidth: 160 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.calibrationRequired}
                    onChange={e => set("calibrationRequired", e.target.checked)} />
                  <span className="form-label" style={{ margin: 0 }}>Calibration Required</span>
                </label>
              </div>
            </div>

            {form.calibrationRequired && (
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label className="form-label">Calibration Frequency</label>
                  <select className="form-select" value={form.calibrationFrequency}
                    onChange={e => set("calibrationFrequency", e.target.value)}>
                    <option value="">— Select —</option>
                    {FREQ_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Calibration Type</label>
                  <select className="form-select" value={form.calibrationType}
                    onChange={e => set("calibrationType", e.target.value)}>
                    <option value="">— Select —</option>
                    <option value="INTERNAL">Internal</option>
                    <option value="EXTERNAL">External</option>
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Expiry Date</label>
                  <input className="form-input" type="date" value={form.expiryDate}
                    onChange={e => set("expiryDate", e.target.value)} />
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-input" rows={2} value={form.notes}
                onChange={e => set("notes", e.target.value)} />
            </div>

            {error && <div className="alert-warn">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Equipment"}
            </button>
          </div>
        </form>
      </div>
  );
}
